// pages/BandRecommendationPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import BandRecommendationForm from '../components/BandRecommendationForm';
import { useAuth } from '../contexts/AuthContext';
import { BandRecommendation } from '../types/BandRecommendations';

const BandRecommendationPage: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const initialStep = searchParams.get('step') || 0;

  const [band, setBand] = useState<BandRecommendation | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchBand = async () => {
      try {
        const response = await axios.get<BandRecommendation>(
          `${process.env.REACT_APP_API_URL}/api/bandrecommendations/${id}`
        );
        setBand(response.data);
      } catch {
        setError('Failed to load band. It may not exist or you may not have access.');
      } finally {
        setLoading(false);
      }
    };
    fetchBand();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );
  }

  // Edit mode: id present and band loaded
  if (id && band) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <BandRecommendationForm
            initialFormData={band}
            isEditMode={true}
            formType="staff_entry"
            initialStep={initialStep}
          />
        </Box>
      </Container>
    );
  }

  // New band submission
  const formType = (user?.role === 'admin' || user?.role === 'gm') ? 'staff_entry' : 'recommendation';
  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <BandRecommendationForm formType={formType} />
      </Box>
    </Container>
  );
};

export default BandRecommendationPage;
